import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDoc,
  setDoc,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';
import { logActivity, ACTIVITY_TYPES, createOrderActivity } from './activityService';
import { cleanupOrderPhotos } from './photoStorageService';
import { syncBlacklistToIndexedDB } from './blacklistService';
import { matchesSearchTerm } from './searchUtils';
import { isCacheStale, recordDataSync } from './cacheService';
import { openIndexedDBWithStores } from './indexedDBUtils';
import { conditionalLog } from './consoleLogger';

/**
 * Comprehensive Order Service for STB Workshop Management
 * Handles all order-related operations with local-first architecture
 * Prioritizes minimal Firestore reads and aggressive IndexedDB caching
 */

// ===== ORDER CRUD OPERATIONS =====


/**
 * Gets all orders with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of order objects
 */
export const getAllOrders = async (forceRefresh = false) => {
  try {
    // If not forcing refresh, try IndexedDB first but check cache freshness
    if (!forceRefresh) {
      const cachedOrders = await getAllOrdersFromIndexedDB();
      if (cachedOrders && cachedOrders.length > 0) {
        // Check if cache is stale
        if (!isCacheStale('orders')) {
          conditionalLog(`Using cached orders data (${cachedOrders.length} orders)`);
          
          // Deserialize dates from cached data
          const deserializedOrders = cachedOrders.map(order => deserializeDates(order));
          
          // Sort by fecha (newest first)
          deserializedOrders.sort((a, b) => {
            const dateA = a.fecha instanceof Date ? a.fecha : new Date(a.fecha || 0);
            const dateB = b.fecha instanceof Date ? b.fecha : new Date(b.fecha || 0);
            return dateB - dateA;
          });
          
          return deserializedOrders;
        } else {
          conditionalLog('Cached orders are stale, fetching fresh data from Firestore...');
        }
      } else {
        conditionalLog('No cached orders found, fetching from Firestore...');
      }
    } else {
      conditionalLog('Force refresh requested, fetching from Firestore...');
    }
    
    // Cache miss or force refresh - fetch from Firestore
    return await fetchAndCacheOrders();
    
  } catch (error) {
    console.error('Error getting orders:', error);
    
    // Fallback to cached data if Firestore fails
    try {
      const cachedOrders = await getAllOrdersFromIndexedDB();
      if (cachedOrders && cachedOrders.length > 0) {
        conditionalLog('Using cached orders as fallback');
        return cachedOrders.map(order => deserializeDates(order));
      }
    } catch (cacheError) {
      console.error('Cache fallback also failed:', cacheError);
    }
    
    return [];
  }
};

/**
 * Fetches orders from Firestore and updates IndexedDB cache
 * @returns {Promise<Array>} Array of order objects
 */
const fetchAndCacheOrders = async () => {
  try {
    // Use simple query without orderBy to avoid index requirements
    const q = query(collection(db, 'orders'));
    
    const querySnapshot = await getDocs(q);
    let orders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually by fecha (newest first) to avoid index requirements
    orders.sort((a, b) => {
      const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0);
      const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    // Populate orders with client and technician names for display
    const populatedOrders = await populateOrdersWithNames(orders);
    
    // Sync with IndexedDB for local search
    await syncOrdersToIndexedDB(populatedOrders);
    
    // Record cache sync time
    recordDataSync('orders');
    
    // Also sync blacklist incidents for offline access
    await syncBlacklistIncidents();
    
    conditionalLog(`Fetched ${populatedOrders.length} orders from Firestore and updated cache`);
    return populatedOrders;
    
  } catch (error) {
    console.error('Error fetching orders from Firestore:', error);
    
    // Handle case where collection doesn't exist yet
    conditionalLog('Orders collection may not exist yet, starting with empty list');
    return [];
  }
};

/**
 * Creates a new order with auto-generated folio
 * Only folio and fecha are required, all other fields optional
 * @param {Object} orderData - Order data object
 * @returns {Promise<Object>} Created order with ID
 */
export const createOrder = async (orderData) => {
  try {
    // Validate required fields
    if (!orderData.fecha) {
      throw new Error('La fecha es requerida');
    }
    
    if (!orderData.tipo_de_orden) {
      throw new Error('El tipo de orden es requerido');
    }
    
    // Validate order type
    const validTypes = ['Reparación', 'Ensamble', 'Garantía', 'Fabricación'];
    if (!validTypes.includes(orderData.tipo_de_orden)) {
      throw new Error('Tipo de orden inválido');
    }
    
    // Get folio number - use provided folio or generate new one
    let folio;
    
    if (orderData.folio && orderData.folio > 0) {
      // Use manually provided folio
      folio = parseInt(orderData.folio);
      conditionalLog('Using manually provided folio:', folio);
      
      // Validate manual folio is unique
      const isUnique = await validateFolioUnique(folio);
      if (!isUnique) {
        throw new Error('El folio ya existe. Por favor, ingrese un folio único.');
      }
      
      // Update counter if manual folio is higher than current counter
      try {
        await updateFolioCounterIfHigher(folio);
      } catch (error) {
        console.warn('Could not update folio counter:', error.message);
        // Don't fail the order creation if counter update fails
      }
    } else {
      // Auto-generate folio
      conditionalLog('Auto-generating folio...');
      try {
        folio = await getNextFolio();
      } catch (error) {
        if (error.code === 'permission-denied') {
          throw new Error('No se puede generar el folio automáticamente. Por favor, ingrese manualmente el folio.');
        } else if (error.code === 'failed-precondition') {
          throw new Error('Detectado conflicto de actualización simultánea. Por favor, intente nuevamente.');
        } else {
          throw new Error('Error al generar el folio: ' + error.message);
        }
      }
    }
    
    // Prepare order data with defaults
    const newOrderData = {
      // Required fields
      folio,
      fecha: orderData.fecha instanceof Date ? Timestamp.fromDate(orderData.fecha) : orderData.fecha,
      tipo_de_orden: orderData.tipo_de_orden,
      
      // Common fields (optional)
      equipos: orderData.equipos || [],
      tecnicos: orderData.tecnicos || [],
      observaciones_detalle: orderData.observaciones_detalle || '',
      fecha_conclusion_servicio: orderData.fecha_conclusion_servicio || null,
      status_orden: orderData.status_orden || getDefaultStatus(orderData.tipo_de_orden),
      
      // Type-specific fields based on order type
      ...getTypeSpecificFields(orderData),
      
      // System fields
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // Check client blacklist if cliente is provided
    if (newOrderData.cliente) {
      const isBlacklisted = await checkClientBlacklist(newOrderData.cliente);
      if (isBlacklisted) {
        console.warn(`Cliente ${newOrderData.cliente} está en lista negra`);
        // Allow creation but flag it
        newOrderData.cliente_blacklisted = true;
      }
    }
    
    const docRef = await addDoc(collection(db, 'orders'), newOrderData);
    
    const createdOrder = {
      id: docRef.id,
      ...newOrderData
    };
    
    // Update IndexedDB with fresh data from Firestore
    const allOrders = await getAllOrders(true); // Force refresh to get updated data
    await syncOrdersToIndexedDB(allOrders);
    // Cache timestamp functionality removed to prevent excessive reads
    
    // Log activity
    try {
      await logActivity(
        ACTIVITY_TYPES.ORDER_CREATED,
        createOrderActivity('created', folio),
        {
          orderId: docRef.id,
          folio: folio,
          tipo: orderData.tipo_de_orden,
          clientName: newOrderData.cliente || newOrderData.clienteName || 'Sin cliente'
        }
      );
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't fail order creation if activity logging fails
    }
    
    return createdOrder;
  } catch (error) {
    console.error('Error creating order:', error);
    throw new Error(error.message || 'Error al crear la orden');
  }
};

/**
 * Updates an existing order
 * Validates folio uniqueness if folio is being changed
 * @param {string} orderId - Order ID to update
 * @param {Object} orderData - Updated order data
 * @returns {Promise<Object>} Updated order object
 */
export const updateOrder = async (orderId, orderData) => {
  try {
    // Get current order data
    const orderDoc = await getDoc(doc(db, 'orders', orderId));
    if (!orderDoc.exists()) {
      throw new Error('Orden no encontrada');
    }
    
    const currentOrder = orderDoc.data();
    
    // If folio is being changed, validate uniqueness
    if (orderData.folio && orderData.folio !== currentOrder.folio) {
      const isUnique = await validateFolioUnique(orderData.folio, orderId);
      if (!isUnique) {
        throw new Error('El folio ya existe');
      }
    }
    
    // Prepare update data
    const updateData = {
      ...orderData,
      updatedAt: Timestamp.now()
    };
    
    // Convert fecha to Timestamp if it's a Date
    if (updateData.fecha instanceof Date) {
      updateData.fecha = Timestamp.fromDate(updateData.fecha);
    }
    
    // Convert fecha_conclusion_servicio to Timestamp if it's a Date
    if (updateData.fecha_conclusion_servicio instanceof Date) {
      updateData.fecha_conclusion_servicio = Timestamp.fromDate(updateData.fecha_conclusion_servicio);
    }
    
    // Check client blacklist if cliente is being updated
    if (updateData.cliente && updateData.cliente !== currentOrder.cliente) {
      const isBlacklisted = await checkClientBlacklist(updateData.cliente);
      updateData.cliente_blacklisted = isBlacklisted;
    }
    
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, updateData);
    
    // Get updated order
    const updatedDoc = await getDoc(orderRef);
    const updatedOrder = { id: updatedDoc.id, ...updatedDoc.data() };
    
    // Update IndexedDB with fresh data from Firestore
    const allOrders = await getAllOrders(true); // Force refresh to get updated data
    await syncOrdersToIndexedDB(allOrders);
    // Cache timestamp functionality removed to prevent excessive reads
    
    // Log activity
    try {
      const folio = updatedOrder.folio || currentOrder.folio;
      
      // Check if status changed specifically
      if (orderData.status_orden && orderData.status_orden !== currentOrder.status_orden) {
        await logActivity(
          ACTIVITY_TYPES.ORDER_STATUS_CHANGED,
          createOrderActivity('status_changed', folio, `${currentOrder.status_orden} → ${orderData.status_orden}`),
          {
            orderId: orderId,
            folio: folio,
            oldStatus: currentOrder.status_orden,
            newStatus: orderData.status_orden
          }
        );
      } else {
        await logActivity(
          ACTIVITY_TYPES.ORDER_UPDATED,
          createOrderActivity('updated', folio),
          {
            orderId: orderId,
            folio: folio,
            updatedFields: Object.keys(orderData).filter(key => key !== 'updatedAt')
          }
        );
      }
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't fail order update if activity logging fails
    }
    
    return updatedOrder;
  } catch (error) {
    console.error('Error updating order:', error);
    throw new Error(error.message || 'Error al actualizar la orden');
  }
};

/**
 * Deletes an order (admin only)
 * @param {string} orderId - Order ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteOrder = async (orderId) => {
  try {
    // Get order info for logging before deletion
    let orderFolio = orderId;
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (orderDoc.exists()) {
        orderFolio = orderDoc.data().folio || orderId;
      }
    } catch (error) {
      console.warn('Could not fetch order folio for logging:', error);
    }
    
    // First, clean up all photos for this order to prevent orphaned files
    try {
      const photoCleanupResult = await cleanupOrderPhotos(orderId);
      conditionalLog(`Photo cleanup for order ${orderFolio}:`, photoCleanupResult);
    } catch (photoError) {
      console.error('Error cleaning up photos for order:', photoError);
      // Don't fail the order deletion if photo cleanup fails
      // This ensures order can still be deleted even if storage cleanup has issues
    }
    
    // Delete the order document from Firestore
    await deleteDoc(doc(db, 'orders', orderId));
    
    // Update local IndexedDB cache with fresh data from Firestore
    const allOrders = await getAllOrders(true); // Force refresh to get updated data
    await syncOrdersToIndexedDB(allOrders);
    
    // Log deletion activity
    try {
      await logActivity(
        'order_deleted', // Using simple string since ACTIVITY_TYPES doesn't have DELETE
        `Orden eliminada: ${orderFolio}`,
        {
          orderId: orderId,
          folio: orderFolio
        }
      );
    } catch (error) {
      console.error('Error logging order deletion activity:', error);
      // Don't fail deletion if activity logging fails
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting order:', error);
    throw new Error('Error al eliminar la orden');
  }
};

// ===== FOLIO MANAGEMENT =====

/**
 * Initializes the folio counter if it doesn't exist
 * @returns {Promise<void>}
 */
const initializeFolioCounter = async () => {
  try {
    const counterRef = doc(db, 'folio_counter', 'current');
    await setDoc(counterRef, {
      current: 0,
      createdAt: Timestamp.now(),
      description: 'Sequential folio counter for orders'
    }, { merge: true });
    conditionalLog('Folio counter initialized');
  } catch (error) {
    console.error('Error initializing folio counter:', error);
  }
};

/**
 * Gets the next sequential folio number using atomic transaction
 * @returns {Promise<number>} Next folio number
 */
/**
 * Updates the folio counter only if the provided folio is higher than current counter
 * @param {number} folio - Manual folio to check against
 * @returns {Promise<boolean>} True if counter was updated
 */
const updateFolioCounterIfHigher = async (folio) => {
  try {
    const counterRef = doc(db, 'folio_counter', 'current');
    
    return await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCounter = 0;
      if (counterDoc.exists()) {
        currentCounter = counterDoc.data().current || 0;
      }
      
      if (folio > currentCounter) {
        // Update counter to the manual folio value
        transaction.set(counterRef, { 
          current: folio,
          lastUpdated: Timestamp.now(),
          updatedBy: 'manual_folio'
        }, { merge: true });
        conditionalLog(`Folio counter updated from ${currentCounter} to ${folio}`);
        return true;
      } else {
        conditionalLog(`Manual folio ${folio} is not higher than current counter ${currentCounter}, counter not updated`);
        return false;
      }
    });
  } catch (error) {
    console.error('Error updating folio counter:', error);
    throw new Error('Could not update folio counter');
  }
};

export const getNextFolio = async () => {
  try {
    const counterRef = doc(db, 'folio_counter', 'current');
    
    return await runTransaction(db, async (transaction) => {
      // First, get all existing folios to find the actual next available folio
      const ordersQuery = query(collection(db, 'orders'));
      const ordersSnapshot = await getDocs(ordersQuery);
      
      // Extract all folio numbers and sort them
      const existingFolios = ordersSnapshot.docs
        .map(doc => parseInt(doc.data().folio) || 0)
        .filter(folio => folio > 0)
        .sort((a, b) => a - b);
      
      let newFolio = 1;
      
      if (existingFolios.length === 0) {
        // No orders exist yet, start with 1
        newFolio = 1;
      } else {
        // Find the first gap in the sequence or use next number after highest
        for (let i = 0; i < existingFolios.length; i++) {
          const expectedFolio = i + 1;
          if (existingFolios[i] !== expectedFolio) {
            // Found a gap, use this folio
            newFolio = expectedFolio;
            break;
          }
        }
        
        // If no gap found, use next number after the highest folio
        if (newFolio === 1 && existingFolios.length > 0) {
          newFolio = existingFolios[existingFolios.length - 1] + 1;
        }
      }
      
      // Update counter to match the actual next folio for future reference
      transaction.set(counterRef, { 
        current: newFolio,
        lastUpdated: Timestamp.now(),
        lastScanCount: existingFolios.length
      }, { merge: true });
      
      return newFolio;
    });
  } catch (error) {
    console.error('Error getting next folio:', error);
    console.error('Error details:', error.code, error.message);
    
    // Check if this is a permission error
    if (error.code === 'permission-denied') {
      console.warn('Permission denied for folio counter. Firestore rules may need adjustment or user role may not be properly set.');
      throw new Error('PERMISSION_DENIED');
    }
    
    // Check if this is a failed precondition (concurrent update)
    if (error.code === 'failed-precondition') {
      console.warn('Concurrent update conflict on folio counter. This usually means multiple tabs or rapid clicks.');
      throw new Error('CONCURRENT_UPDATE');
    }
    
    // For other errors, try to initialize counter and retry once
    try {
      await initializeFolioCounter();
      return 1; // Start with folio 1
    } catch (initError) {
      console.error('Failed to initialize folio counter:', initError);
      throw new Error('INITIALIZATION_FAILED');
    }
  }
};

/**
 * Validates that a folio is unique
 * @param {number} folio - Folio to validate
 * @param {string} excludeId - Order ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if folio is unique
 */
export const validateFolioUnique = async (folio, excludeId = null) => {
  try {
    const q = query(
      collection(db, 'orders'),
      where('folio', '==', folio)
    );
    
    const querySnapshot = await getDocs(q);
    
    // If no documents found, folio is unique
    if (querySnapshot.empty) {
      return true;
    }
    
    // If documents found, check if any are different from excluded ID
    const existingOrders = querySnapshot.docs.filter(doc => doc.id !== excludeId);
    return existingOrders.length === 0;
  } catch (error) {
    console.error('Error validating folio:', error);
    return false;
  }
};

// ===== CLIENT BLACKLIST INTEGRATION =====

/**
 * Checks if a client is blacklisted using local data
 * @param {string} clientId - Client ID to check
 * @returns {Promise<boolean>} True if client is blacklisted
 */
export const checkClientBlacklist = async (clientId) => {
  try {
    // Try to get from IndexedDB first for speed
    const localClient = await getClientFromIndexedDB(clientId);
    if (localClient) {
      return localClient.isBlacklisted === true;
    }
    
    // Fallback to Firestore
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (!clientDoc.exists()) {
      return false;
    }
    
    return clientDoc.data().isBlacklisted === true;
  } catch (error) {
    console.error('Error checking client blacklist:', error);
    return false; // Default to not blacklisted if error
  }
};

// ===== SEARCH AND FILTERING =====

/**
 * Searches orders in IndexedDB for instant results
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered orders
 */
export const searchOrdersLocal = async (searchTerm) => {
  try {
    if (!searchTerm.trim()) {
      return await getAllOrdersFromIndexedDB();
    }
    
    const orders = await getAllOrdersFromIndexedDB();
    // Get brands for dynamic lookup
    const { getAllBrands } = await import('./brandService');
    const brands = await getAllBrands();
    
    // Get clients for contact search
    const { getAllClients } = await import('./clientService');
    const clients = await getAllClients();
    const clientsMap = new Map(clients.map(client => [client.id, client]));
    
    const results = orders.filter(order => {
      // Search in folio
      if (order.folio && order.folio.toString().includes(searchTerm)) {
        return true;
      }
      
      // Search in client name (populated)
      if (order.clienteName && matchesSearchTerm(order.clienteName, searchTerm)) {
        return true;
      }
      
      // Search in all client contacts (including non-main contacts)
      if (order.cliente && clientsMap.has(order.cliente)) {
        const client = clientsMap.get(order.cliente);
        
        // Search in all contacts of this client
        if (client.contactos) {
          for (const contact of client.contactos) {
            // Search contact name
            if (contact.nombre && matchesSearchTerm(contact.nombre, searchTerm)) {
              return true;
            }
            // Search contact phone
            if (contact.telefono && matchesSearchTerm(contact.telefono, searchTerm)) {
              return true;
            }
            // Search contact email
            if (contact.email && matchesSearchTerm(contact.email, searchTerm)) {
              return true;
            }
          }
        }
        
        // Also search company name and other client fields
        if (client.nombreEmpresa && matchesSearchTerm(client.nombreEmpresa, searchTerm)) {
          return true;
        }
        if (client.direccion && matchesSearchTerm(client.direccion, searchTerm)) {
          return true;
        }
      }
      
      // Search in equipment data (comprehensive)
      if (order.equipos && order.equipos.some(equipo => {
        // Search in basic equipment fields
        if ((equipo.modelo && matchesSearchTerm(equipo.modelo, searchTerm)) ||
            (equipo.descripcion && matchesSearchTerm(equipo.descripcion, searchTerm)) ||
            (equipo.series && matchesSearchTerm(equipo.series, searchTerm)) ||
            (equipo.notes && matchesSearchTerm(equipo.notes, searchTerm))) {
          return true;
        }
        
        // Search in catalog data if available
        if (equipo.catalogData) {
          // Handle brand search with dynamic lookup (preferred) or legacy marca field
          let brandMatch = false;
          if (equipo.catalogData.marcaId) {
            const brand = brands.find(b => b.id === equipo.catalogData.marcaId);
            if (brand && brand.nombre && matchesSearchTerm(brand.nombre, searchTerm)) {
              brandMatch = true;
            }
          } else if (equipo.catalogData.marca && matchesSearchTerm(equipo.catalogData.marca, searchTerm)) {
            // Legacy support for old orders with marca field
            brandMatch = true;
          }
          
          if (brandMatch ||
              (equipo.catalogData.tipo && matchesSearchTerm(equipo.catalogData.tipo, searchTerm)) ||
              (equipo.catalogData.modelo && matchesSearchTerm(equipo.catalogData.modelo, searchTerm)) ||
              (equipo.catalogData.numeroParte && matchesSearchTerm(equipo.catalogData.numeroParte, searchTerm))) {
            return true;
          }
          
          // Search in alternate part numbers
          if (equipo.catalogData.partesAlternativas && Array.isArray(equipo.catalogData.partesAlternativas)) {
            const altPartMatch = equipo.catalogData.partesAlternativas.some(altPart => 
              altPart && altPart.trim() && matchesSearchTerm(altPart, searchTerm)
            );
            if (altPartMatch) {
              return true;
            }
          }
          
          // Search in specifications
          if (equipo.catalogData.especificaciones) {
            const specs = Object.values(equipo.catalogData.especificaciones);
            if (specs.some(spec => spec && matchesSearchTerm(spec.toString(), searchTerm))) {
              return true;
            }
          }
        }
        
        return false;
      })) {
        return true;
      }
      
      // Search in technician names (populated)
      if (order.technicianNames && order.technicianNames.some(name => 
        matchesSearchTerm(name, searchTerm)
      )) {
        return true;
      }
      
      // Search in status
      if (order.status_orden && matchesSearchTerm(order.status_orden, searchTerm)) {
        return true;
      }
      
      // Search in order type
      if (order.tipo_de_orden && matchesSearchTerm(order.tipo_de_orden, searchTerm)) {
        return true;
      }
      
      return false;
    });
    
    // Sort results by folio (descending - highest to lowest)
    results.sort((a, b) => {
      const folioA = parseInt(a.folio) || 0;
      const folioB = parseInt(b.folio) || 0;
      return folioB - folioA; // Descending order
    });
    
    return results;
  } catch (error) {
    console.error('Error searching orders locally:', error);
    return [];
  }
};

// ===== HELPER FUNCTIONS =====

/**
 * Gets default status for order type
 * @param {string} orderType - Order type
 * @returns {string} Default status
 */
const getDefaultStatus = (orderType) => {
  return 'Abierta'; // All order types start with 'Abierta'
};

/**
 * Gets type-specific fields for order
 * @param {Object} orderData - Order data
 * @returns {Object} Type-specific fields
 */
const getTypeSpecificFields = (orderData) => {
  const fields = {};
  
  switch (orderData.tipo_de_orden) {
    case 'Reparación':
      fields.cliente = orderData.cliente || null;
      fields.comentarios_cliente = orderData.comentarios_cliente || '';
      fields.resultados_revision = orderData.resultados_revision || '';
      fields.folio_cotizacion = orderData.folio_cotizacion || '';
      fields.importe = orderData.importe || null;
      fields.autorizacion = orderData.autorizacion || false;
      fields.fecha_autorizacion = orderData.fecha_autorizacion || null;
      fields.folio_orden_compra = orderData.folio_orden_compra || '';
      fields.folio_factura_contpaq = orderData.folio_factura_contpaq || '';
      fields.estatus_pago = orderData.estatus_pago || '';
      fields.fecha_entrega_equipo = orderData.fecha_entrega_equipo || null;
      fields.quien_recibe_equipo = orderData.quien_recibe_equipo || '';
      break;
      
    case 'Ensamble':
      fields.cliente = orderData.cliente || null;
      fields.folio_cotizacion = orderData.folio_cotizacion || '';
      fields.importe = orderData.importe || null;
      fields.autorizacion = orderData.autorizacion || false;
      fields.fecha_autorizacion = orderData.fecha_autorizacion || null;
      fields.folio_orden_compra = orderData.folio_orden_compra || '';
      fields.folio_factura_contpaq = orderData.folio_factura_contpaq || '';
      fields.folio_salida_contpaq = orderData.folio_salida_contpaq || '';
      fields.estatus_pago = orderData.estatus_pago || '';
      fields.fecha_entrega_equipo = orderData.fecha_entrega_equipo || null;
      fields.quien_recibe_equipo = orderData.quien_recibe_equipo || '';
      break;
      
    case 'Garantía':
      fields.cliente = orderData.cliente || null;
      fields.comentarios_cliente = orderData.comentarios_cliente || '';
      fields.resultados_revision = orderData.resultados_revision || '';
      fields.fecha_compra_equipo = orderData.fecha_compra_equipo || null;
      fields.folio_compra = orderData.folio_compra || '';
      fields.rma = orderData.rma || '';
      fields.fecha_envio_fabricante = orderData.fecha_envio_fabricante || null;
      fields.fecha_respuesta = orderData.fecha_respuesta || null;
      fields.veredicto = orderData.veredicto || '';
      fields.detalle_veredicto = orderData.detalle_veredicto || '';
      fields.folio_cotizacion = orderData.folio_cotizacion || '';
      fields.importe = orderData.importe || null;
      fields.autorizacion = orderData.autorizacion || false;
      fields.fecha_autorizacion = orderData.fecha_autorizacion || null;
      fields.folio_orden_compra = orderData.folio_orden_compra || '';
      fields.folio_factura_contpaq = orderData.folio_factura_contpaq || '';
      fields.estatus_pago = orderData.estatus_pago || '';
      fields.fecha_entrega_equipo = orderData.fecha_entrega_equipo || null;
      fields.quien_recibe_equipo = orderData.quien_recibe_equipo || '';
      break;
      
    case 'Fabricación':
      // No additional fields for Fabricación - only common fields
      break;
      
    default:
      break;
  }
  
  return fields;
};

/**
 * Gets valid status options for order type
 * @param {string} orderType - Order type
 * @returns {Array} Array of valid status options
 */
export const getValidStatusOptions = (orderType) => {
  const statusOptions = {
    'Reparación': [
      'Abierta', 'Diagnóstico', 'Cotización', 'Comunicación con cliente',
      'En espera de piezas', 'En proceso', 'Empaque', 'Lista para entrega',
      'Cancelada', 'Terminada'
    ],
    'Ensamble': [
      'Abierta', 'Comunicación con cliente', 'En espera de piezas',
      'En proceso', 'Empaque', 'Lista para entrega', 'Cancelada', 'Terminada'
    ],
    'Garantía': [
      'Abierta', 'Diagnóstico', 'Comunicación con cliente', 'En espera de piezas',
      'En proceso', 'Lista para entrega', 'Cancelada', 'Terminada'
    ],
    'Fabricación': [
      'Abierta', 'En espera de piezas', 'En proceso', 'Cancelada', 'Terminada'
    ]
  };
  
  return statusOptions[orderType] || ['Abierta'];
};

// ===== ORDER POPULATION FUNCTIONS =====

/**
 * Gets cached reference data from IndexedDB or Firestore batch
 * Optimized to prevent excessive individual reads
 * @param {Array} clientIds - Client IDs to fetch
 * @param {Array} technicianIds - Technician IDs to fetch  
 * @param {Array} equipmentIds - Equipment catalog IDs to fetch
 * @returns {Promise<Object>} Cached reference data
 */
const getCachedReferenceData = async (clientIds = [], technicianIds = [], equipmentIds = []) => {
  const referenceData = {
    clients: new Map(),
    technicians: new Map(),
    equipment: new Map()
  };
  
  try {
    // Try to get from IndexedDB cache first
    const { clients: cachedClients, technicians: cachedTechnicians, equipment: cachedEquipment } = await getReferenceDataFromCache(
      clientIds, technicianIds, equipmentIds
    );
    
    // Add cached data to maps
    cachedClients.forEach(client => referenceData.clients.set(client.id, client));
    cachedTechnicians.forEach(tech => referenceData.technicians.set(tech.id, tech));
    cachedEquipment.forEach(equip => referenceData.equipment.set(equip.id, equip));
    
    // Find missing IDs that need to be fetched from Firestore
    const missingClientIds = clientIds.filter(id => !referenceData.clients.has(id));
    const missingTechnicianIds = technicianIds.filter(id => !referenceData.technicians.has(id));
    const missingEquipmentIds = equipmentIds.filter(id => !referenceData.equipment.has(id));
    
    // Batch fetch missing data from Firestore only if needed
    if (missingClientIds.length || missingTechnicianIds.length || missingEquipmentIds.length) {
      conditionalLog(`Batch fetching missing data: ${missingClientIds.length} clients, ${missingTechnicianIds.length} technicians, ${missingEquipmentIds.length} equipment`);
      
      const batchPromises = [];
      
      // Batch fetch missing clients
      if (missingClientIds.length > 0) {
        batchPromises.push(
          Promise.all(
            missingClientIds.map(async (clientId) => {
              try {
                const clientDoc = await getDoc(doc(db, 'clients', clientId));
                if (clientDoc.exists()) {
                  const clientData = { id: clientId, ...clientDoc.data() };
                  referenceData.clients.set(clientId, clientData);
                  return clientData;
                }
              } catch (error) {
                console.error(`Error fetching client ${clientId}:`, error);
              }
              return null;
            })
          )
        );
      }
      
      // Batch fetch missing technicians
      if (missingTechnicianIds.length > 0) {
        batchPromises.push(
          Promise.all(
            missingTechnicianIds.map(async (techId) => {
              try {
                const techDoc = await getDoc(doc(db, 'technicians', techId));
                if (techDoc.exists()) {
                  const techData = { id: techId, ...techDoc.data() };
                  referenceData.technicians.set(techId, techData);
                  return techData;
                }
              } catch (error) {
                console.error(`Error fetching technician ${techId}:`, error);
              }
              return null;
            })
          )
        );
      }
      
      // Batch fetch missing equipment using equipmentService for populated data
      if (missingEquipmentIds.length > 0) {
        batchPromises.push(
          (async () => {
            try {
              // Use equipmentService to get properly populated equipment data
              const { getAllEquipment } = await import('./equipmentService');
              const allEquipment = await getAllEquipment(true); // Include archived
              
              // Filter to only the equipment we need and add to reference data
              missingEquipmentIds.forEach(equipId => {
                const equipData = allEquipment.find(eq => eq.id === equipId);
                if (equipData) {
                  referenceData.equipment.set(equipId, equipData);
                }
              });
              
              return allEquipment.filter(eq => missingEquipmentIds.includes(eq.id));
            } catch (error) {
              console.error('Error fetching equipment using equipmentService:', error);
              
              // Fallback to direct Firestore access if equipmentService fails
              return Promise.all(
                missingEquipmentIds.map(async (equipId) => {
                  try {
                    const equipDoc = await getDoc(doc(db, 'equipment_catalog', equipId));
                    if (equipDoc.exists()) {
                      const equipData = { id: equipId, ...equipDoc.data() };
                      referenceData.equipment.set(equipId, equipData);
                      return equipData;
                    }
                  } catch (fallbackError) {
                    console.error(`Fallback fetch failed for equipment ${equipId}:`, fallbackError);
                  }
                  return null;
                })
              );
            }
          })()
        );
      }
      
      // Wait for all batch operations to complete
      await Promise.all(batchPromises);
      
      // Update cache with newly fetched data for future use
      await updateReferenceDataCache(referenceData);
    }
    
    return referenceData;
  } catch (error) {
    console.error('Error getting cached reference data:', error);
    return referenceData; // Return empty maps if error
  }
};

/**
 * Opens IndexedDB with robust store creation handling
 * @returns {Promise<IDBDatabase>} Database connection
 */
const openIndexedDB = async () => {
  return await openIndexedDBWithStores();
};

/**
 * Legacy IndexedDB opener - kept for reference
 * @returns {Promise<IDBDatabase>} Database connection
 */
const openIndexedDBLegacy = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('STBWorkshop', 23);
    
    request.onerror = () => {
      console.error('Error opening IndexedDB:', request.error);
      reject(request.error);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      conditionalLog('Upgrading IndexedDB schema...');
      
      // Create all required object stores if they don't exist
      if (!db.objectStoreNames.contains('orders')) {
        conditionalLog('Creating orders object store');
        db.createObjectStore('orders', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('clients')) {
        conditionalLog('Creating clients object store');
        db.createObjectStore('clients', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('technicians')) {
        conditionalLog('Creating technicians object store');
        db.createObjectStore('technicians', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('equipment_catalog')) {
        conditionalLog('Creating equipment_catalog object store');
        db.createObjectStore('equipment_catalog', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

/**
 * Gets reference data from IndexedDB cache
 * @param {Array} clientIds - Client IDs
 * @param {Array} technicianIds - Technician IDs
 * @param {Array} equipmentIds - Equipment IDs
 * @returns {Promise<Object>} Cached data
 */
const getReferenceDataFromCache = async (clientIds, technicianIds, equipmentIds) => {
  const result = {
    clients: [],
    technicians: [],
    equipment: []
  };
  
  try {
    const db = await openIndexedDB();
    
    return new Promise((resolve) => {
        const promises = [];
        
        // Get cached clients
        if (clientIds.length > 0 && db.objectStoreNames.contains('clients')) {
          const clientPromise = new Promise((clientResolve) => {
            const transaction = db.transaction(['clients'], 'readonly');
            const store = transaction.objectStore('clients');
            const clientResults = [];
            
            let pending = clientIds.length;
            clientIds.forEach(id => {
              const request = store.get(id);
              request.onsuccess = () => {
                if (request.result) {
                  clientResults.push(request.result);
                }
                pending--;
                if (pending === 0) {
                  result.clients = clientResults;
                  clientResolve();
                }
              };
              request.onerror = () => {
                pending--;
                if (pending === 0) {
                  result.clients = clientResults;
                  clientResolve();
                }
              };
            });
          });
          promises.push(clientPromise);
        }
        
        // Get cached technicians
        if (technicianIds.length > 0 && db.objectStoreNames.contains('technicians')) {
          const techPromise = new Promise((techResolve) => {
            const transaction = db.transaction(['technicians'], 'readonly');
            const store = transaction.objectStore('technicians');
            const techResults = [];
            
            let pending = technicianIds.length;
            technicianIds.forEach(id => {
              const request = store.get(id);
              request.onsuccess = () => {
                if (request.result) {
                  techResults.push(request.result);
                }
                pending--;
                if (pending === 0) {
                  result.technicians = techResults;
                  techResolve();
                }
              };
              request.onerror = () => {
                pending--;
                if (pending === 0) {
                  result.technicians = techResults;
                  techResolve();
                }
              };
            });
          });
          promises.push(techPromise);
        }
        
        // Get cached equipment
        if (equipmentIds.length > 0 && db.objectStoreNames.contains('equipment_catalog')) {
          const equipPromise = new Promise((equipResolve) => {
            const transaction = db.transaction(['equipment_catalog'], 'readonly');
            const store = transaction.objectStore('equipment_catalog');
            const equipResults = [];
            
            let pending = equipmentIds.length;
            equipmentIds.forEach(id => {
              const request = store.get(id);
              request.onsuccess = () => {
                if (request.result) {
                  equipResults.push(request.result);
                }
                pending--;
                if (pending === 0) {
                  result.equipment = equipResults;
                  equipResolve();
                }
              };
              request.onerror = () => {
                pending--;
                if (pending === 0) {
                  result.equipment = equipResults;
                  equipResolve();
                }
              };
            });
          });
          promises.push(equipPromise);
        }
        
        // Wait for all cache operations to complete
        Promise.all(promises).then(() => {
          resolve(result);
        }).catch(() => {
          resolve(result); // Return partial results on error
        });
        
        // If no promises, resolve immediately
        if (promises.length === 0) {
          db.close();
          resolve(result);
        }
      });
  } catch (error) {
    console.error('Error getting reference data from cache:', error);
    return result;
  }
};

/**
 * Updates reference data cache in IndexedDB
 * @param {Object} referenceData - Reference data to cache
 */
const updateReferenceDataCache = async (referenceData) => {
  try {
    const db = await openIndexedDB();
    
    if (db) {
      const promises = [];
      
      // Cache clients
      if (referenceData.clients.size > 0 && db.objectStoreNames.contains('clients')) {
        const clientTransaction = db.transaction(['clients'], 'readwrite');
        const clientStore = clientTransaction.objectStore('clients');
        referenceData.clients.forEach(client => {
          clientStore.put(client);
        });
        promises.push(new Promise(resolve => {
          clientTransaction.oncomplete = resolve;
          clientTransaction.onerror = resolve;
        }));
      }
      
      // Cache technicians
      if (referenceData.technicians.size > 0 && db.objectStoreNames.contains('technicians')) {
        const techTransaction = db.transaction(['technicians'], 'readwrite');
        const techStore = techTransaction.objectStore('technicians');
        referenceData.technicians.forEach(tech => {
          techStore.put(tech);
        });
        promises.push(new Promise(resolve => {
          techTransaction.oncomplete = resolve;
          techTransaction.onerror = resolve;
        }));
      }
      
      // Cache equipment
      if (referenceData.equipment.size > 0 && db.objectStoreNames.contains('equipment_catalog')) {
        const equipTransaction = db.transaction(['equipment_catalog'], 'readwrite');
        const equipStore = equipTransaction.objectStore('equipment_catalog');
        referenceData.equipment.forEach(equip => {
          equipStore.put(equip);
        });
        promises.push(new Promise(resolve => {
          equipTransaction.oncomplete = resolve;
          equipTransaction.onerror = resolve;
        }));
      }
      
      Promise.all(promises).then(() => {
        db.close();
      }).catch(error => {
        console.error('Error updating reference data cache:', error);
        db.close();
      });
    }
  } catch (error) {
    console.error('Error updating reference data cache:', error);
  }
};

/**
 * Populates orders with client and technician names for better display
 * OPTIMIZED: Uses batch lookup and caching to prevent excessive Firestore reads
 * @param {Array} orders - Orders to populate
 * @returns {Promise<Array>} Orders with populated names
 */
const populateOrdersWithNames = async (orders) => {
  try {
    if (!orders || orders.length === 0) {
      return orders;
    }
    
    // Collect all unique IDs that need to be fetched
    const clientIds = new Set();
    const technicianIds = new Set();
    const equipmentIds = new Set();
    
    orders.forEach(order => {
      if (order.cliente) {
        clientIds.add(order.cliente);
      }
      if (order.tecnicos && order.tecnicos.length > 0) {
        order.tecnicos.forEach(techId => technicianIds.add(techId));
      }
      if (order.equipos && order.equipos.length > 0) {
        order.equipos.forEach(equipo => {
          if (equipo.catalogId) {
            equipmentIds.add(equipo.catalogId);
          }
        });
      }
    });
    
    // Batch fetch all reference data at once
    const referenceData = await getCachedReferenceData(
      Array.from(clientIds),
      Array.from(technicianIds), 
      Array.from(equipmentIds)
    );
    
    // Populate orders using cached reference data
    const populatedOrders = orders.map(order => {
      const populatedOrder = { ...order };
      
      // Populate client name using cached data
      if (order.cliente && referenceData.clients.has(order.cliente)) {
        const clientData = referenceData.clients.get(order.cliente);
        
        // Find the main contact (esPrincipal: true)
        let mainContact = null;
        if (clientData.contactos && clientData.contactos.length > 0) {
          mainContact = clientData.contactos.find(contact => contact.esPrincipal === true);
          // If no main contact found, use the first one as fallback
          if (!mainContact) {
            mainContact = clientData.contactos[0];
          }
        }
        
        // Build client display name based on business rules
        let clientName = 'Sin nombre';
        
        if (clientData.nombreEmpresa && mainContact?.nombre) {
          // Company with main contact: "Company Name - Contact Name"
          clientName = `${clientData.nombreEmpresa} - ${mainContact.nombre}`;
        } else if (clientData.nombreEmpresa) {
          // Company without contact
          clientName = clientData.nombreEmpresa;
        } else if (mainContact?.nombre) {
          // Individual client: just main contact name
          clientName = mainContact.nombre;
        }
        
        populatedOrder.clienteName = clientName;
        populatedOrder.clientePhone = mainContact?.telefono || null;
        populatedOrder.clienteCompanyName = clientData.nombreEmpresa || null;
        populatedOrder.clienteContactName = mainContact?.nombre || null;
      } else if (order.cliente) {
        populatedOrder.clienteName = `Cliente no encontrado: ${order.cliente}`;
      }
      
      // Populate technician names using cached data
      if (order.tecnicos && order.tecnicos.length > 0) {
        const technicianNames = order.tecnicos.map(techId => {
          if (referenceData.technicians.has(techId)) {
            return referenceData.technicians.get(techId).nombre || 'Sin nombre';
          }
          return 'Sin nombre';
        });
        populatedOrder.technicianNames = technicianNames;
      } else {
        populatedOrder.technicianNames = [];
      }
      
      // Populate equipment catalog data using cached data
      if (order.equipos && order.equipos.length > 0) {
        const populatedEquipment = order.equipos.map(equipo => {
          // If it has catalogId and we have cached data, use it
          if (equipo.catalogId && referenceData.equipment.has(equipo.catalogId)) {
            return {
              ...equipo,
              catalogData: referenceData.equipment.get(equipo.catalogId),
              type: 'catalog'
            };
          }
          
          // Return as exempt equipment if no catalog data
          return {
            ...equipo,
            type: 'exempt'
          };
        });
        
        populatedOrder.equipos = populatedEquipment;
      }
      
      return populatedOrder;
    });
    
    return populatedOrders;
  } catch (error) {
    console.error('Error populating orders with names:', error);
    return orders; // Return original orders if population fails
  }
};

// ===== INDEXEDDB SYNC FUNCTIONS =====

/**
 * Syncs orders data to IndexedDB for local search
 * @param {Array} orders - Orders to sync (should already be populated with names)
 * @returns {Promise<boolean>} Success status
 */
export const syncOrdersToIndexedDB = async (orders) => {
  try {
    const db = await openIndexedDB();
    
    return new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains('orders')) {
        conditionalLog('Orders store not found in IndexedDB');
        db.close();
        resolve(false);
        return;
      }
      
      const transaction = db.transaction(['orders'], 'readwrite');
      const store = transaction.objectStore('orders');
      
      // Clear existing data
      store.clear();
      
      // Add new data with proper date serialization
      orders.forEach(order => {
        // Serialize dates to ISO strings for IndexedDB storage
        const serializedOrder = {
          ...order,
          fecha_creacion: order.fecha_creacion instanceof Date ? order.fecha_creacion.toISOString() : order.fecha_creacion,
          fecha_limite: order.fecha_limite instanceof Date ? order.fecha_limite.toISOString() : order.fecha_limite,
          updatedAt: order.updatedAt instanceof Date ? order.updatedAt.toISOString() : order.updatedAt,
          createdAt: order.createdAt instanceof Date ? order.createdAt.toISOString() : order.createdAt
        };
        store.put(serializedOrder);
      });
      
      transaction.oncomplete = () => {
        conditionalLog(`Synced ${orders.length} orders to IndexedDB`);
        db.close();
        resolve(true);
      };
      
      transaction.onerror = () => {
        console.error('IndexedDB transaction error:', transaction.error);
        db.close();
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('Error syncing orders to IndexedDB:', error);
    return false;
  }
};

/**
 * Gets all orders from IndexedDB
 * @returns {Promise<Array>} Orders from IndexedDB
 */
export const getAllOrdersFromIndexedDB = async () => {
  try {
    const db = await openIndexedDB();
    
    return new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains('orders')) {
        conditionalLog('Orders store not found in IndexedDB, returning empty array');
        db.close();
        resolve([]);
        return;
      }
      
      const transaction = db.transaction(['orders'], 'readonly');
      const store = transaction.objectStore('orders');
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        const result = getAllRequest.result || [];
        conditionalLog(`Retrieved ${result.length} orders from IndexedDB cache`);
        db.close();
        resolve(result);
      };
      
      getAllRequest.onerror = () => {
        console.error('Error reading from orders store:', getAllRequest.error);
        db.close();
        reject(getAllRequest.error);
      };
    });
  } catch (error) {
    console.error('Error getting orders from IndexedDB:', error);
    return [];
  }
};

/**
 * Gets a client from IndexedDB
 * @param {string} clientId - Client ID
 * @returns {Promise<Object|null>} Client data or null
 */
const getClientFromIndexedDB = async (clientId) => {
  try {
    const db = await openIndexedDB();
    
    return new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains('clients')) {
        db.close();
        resolve(null);
        return;
      }
      
      const transaction = db.transaction(['clients'], 'readonly');
      const store = transaction.objectStore('clients');
      const getRequest = store.get(clientId);
      
      getRequest.onsuccess = () => {
        db.close();
        resolve(getRequest.result || null);
      };
      
      getRequest.onerror = () => {
        db.close();
        reject(getRequest.error);
      };
    });
  } catch (error) {
    console.error('Error getting client from IndexedDB:', error);
    return null;
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Converts serialized date objects back to Date objects
 * Handles Firestore Timestamps that have been serialized to IndexedDB
 * @param {Object} obj - Object that may contain serialized dates
 * @returns {Object} Object with dates converted to Date objects
 */
const deserializeDates = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    const value = obj[key];
    
    if (value && typeof value === 'object') {
      // Check if this looks like a serialized Firestore Timestamp
      if (value.seconds && typeof value.seconds === 'number') {
        result[key] = new Date(value.seconds * 1000);
      } else if (Array.isArray(value)) {
        result[key] = value.map(item => deserializeDates(item));
      } else {
        result[key] = deserializeDates(value);
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Syncs blacklist incidents to IndexedDB for offline access
 * @returns {Promise<void>}
 */
const syncBlacklistIncidents = async () => {
  try {
    // Fetch all blacklist incidents from Firestore
    const blacklistQuery = query(collection(db, 'blacklist_incidents'));
    const blacklistSnapshot = await getDocs(blacklistQuery);
    
    const incidents = blacklistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sync to IndexedDB
    await syncBlacklistToIndexedDB(incidents);
    conditionalLog(`Synced ${incidents.length} blacklist incidents to cache`);
  } catch (error) {
    console.error('Error syncing blacklist incidents:', error);
    // Don't throw - this is optional for performance
  }
};

/**
 * Forces a refresh of orders data from Firestore
 * Useful when data might have been updated externally
 * @returns {Promise<Array>} Fresh orders data
 */
export const refreshOrdersData = async () => {
  try {
    conditionalLog('Forcing refresh of orders data from Firestore...');
    return await fetchAndCacheOrders();
  } catch (error) {
    console.error('Error refreshing orders data:', error);
    throw error;
  }
};

/**
 * Formats date to Spanish format (DD/MMM/YYYY)
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else if (date.toDate && typeof date.toDate === 'function') {
    // Firestore Timestamp
    dateObj = date.toDate();
  } else if (typeof date === 'string') {
    // String date from IndexedDB
    dateObj = new Date(date);
  } else if (date && typeof date === 'object' && date.seconds) {
    // Serialized Firestore Timestamp from IndexedDB
    dateObj = new Date(date.seconds * 1000);
  } else {
    // Fallback
    dateObj = new Date(date);
  }
  
  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }
  const months = [
    'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
    'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
  ];
  
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = months[dateObj.getUTCMonth()];
  const year = dateObj.getUTCFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Formats date/time to Spanish locale
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : date.toDate();
  return dateObj.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Gets Spanish display text for order type
 * @param {string} tipo - Order type
 * @returns {string} Spanish display text
 */
export const getOrderTypeDisplayText = (tipo) => {
  const typeNames = {
    'Reparación': 'Reparación',
    'Ensamble': 'Ensamble',
    'Garantía': 'Garantía',
    'Fabricación': 'Fabricación'
  };
  
  return typeNames[tipo] || tipo || 'N/A';
};

/**
 * Gets CSS class for order status
 * @param {string} status - Order status
 * @returns {string} CSS class name
 */
export const getStatusColor = (status) => {
  const statusColors = {
    'Abierta': 'bg-blue-100 text-blue-800',
    'Diagnóstico': 'bg-yellow-100 text-yellow-800',
    'Cotización': 'bg-purple-100 text-purple-800',
    'Comunicación con cliente': 'bg-orange-100 text-orange-800',
    'En espera de piezas': 'bg-gray-100 text-gray-800',
    'En proceso': 'bg-indigo-100 text-indigo-800',
    'Empaque': 'bg-cyan-100 text-cyan-800',
    'Lista para entrega': 'bg-green-100 text-green-800',
    'Cancelada': 'bg-red-100 text-red-800',
    'Terminada': 'bg-emerald-100 text-emerald-800'
  };
  
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

// ===== ARCHIVE OPERATIONS =====

/**
 * Validates if an order meets the requirements for archiving
 * @param {Object} order - Order object to validate
 * @returns {Array} Array of validation issue messages, empty if valid
 */
export const validateOrderForArchiving = (order) => {
  const validationIssues = [];
  const validOrderStatuses = ['Cancelada', 'Terminada'];
  const validPaymentStatuses = ['Pagado', 'Sin cargo'];
  
  if (!validOrderStatuses.includes(order.status_orden)) {
    validationIssues.push(`Estado de la orden debe ser "Cancelada" o "Terminada" (actual: "${order.status_orden || 'Sin estado'}")`);
  }
  
  if (!validPaymentStatuses.includes(order.estatus_pago)) {
    validationIssues.push(`Estado de pago debe ser "Pagado" o "Sin cargo" (actual: "${order.estatus_pago || 'Sin definir'}")`);
  }
  
  return validationIssues;
};

/**
 * Shows archive validation warning and gets user confirmation
 * @param {Object} order - Order object to validate and confirm
 * @returns {Promise<boolean>} True if user confirms to proceed
 */
export const confirmArchiveOrder = async (order) => {
  const validationIssues = validateOrderForArchiving(order);
  
  if (validationIssues.length > 0) {
    const issuesList = validationIssues.map(issue => `• ${issue}`).join('\n');
    const warningMessage = 
      `⚠️ ADVERTENCIA: La orden no cumple los requisitos para ser archivada:\n\n` +
      `${issuesList}\n\n` +
      `¿Desea archivar la orden de todas formas?`;
    
    return window.confirm(warningMessage);
  } else {
    // Standard confirmation for valid orders
    return window.confirm(
      `¿Está seguro de archivar la orden ${order.folio}?\n\n` +
      `La orden se moverá a la sección de archivadas.`
    );
  }
};

/**
 * Archives an order
 * @param {string} orderId - Order ID to archive
 * @returns {Promise<Object>} Updated order object
 */
export const archiveOrder = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    
    // Get order data first to access folio
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) {
      throw new Error('Orden no encontrada');
    }
    const orderData = orderDoc.data();
    
    const updateData = {
      isArchived: true,
      archivedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(orderRef, updateData);
    
    // Update IndexedDB with fresh data from Firestore
    const allOrders = await getAllOrders(true); // Force refresh to get updated data
    await syncOrdersToIndexedDB(allOrders);
    // Cache timestamp functionality removed to prevent excessive reads
    
    // Log activity with folio
    await logActivity(ACTIVITY_TYPES.ORDER_ARCHIVED, `Orden #${orderData.folio} archivada`, {
      orderId,
      folio: orderData.folio
    });
    
    // Get updated order
    const updatedOrderDoc = await getDoc(orderRef);
    return { id: updatedOrderDoc.id, ...updatedOrderDoc.data() };
  } catch (error) {
    console.error('Error archiving order:', error);
    throw new Error('Error al archivar la orden');
  }
};

/**
 * Unarchives an order
 * @param {string} orderId - Order ID to unarchive
 * @returns {Promise<Object>} Updated order object
 */
export const unarchiveOrder = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    
    // Get order data first to access folio
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) {
      throw new Error('Orden no encontrada');
    }
    const orderData = orderDoc.data();
    
    const updateData = {
      isArchived: false,
      archivedAt: null,
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(orderRef, updateData);
    
    // Update IndexedDB with fresh data from Firestore
    const allOrders = await getAllOrders(true); // Force refresh to get updated data
    await syncOrdersToIndexedDB(allOrders);
    // Cache timestamp functionality removed to prevent excessive reads
    
    // Log activity with folio
    await logActivity(ACTIVITY_TYPES.ORDER_UNARCHIVED, `Orden #${orderData.folio} desarchivada`, {
      orderId,
      folio: orderData.folio
    });
    
    // Get updated order
    const updatedOrderDoc = await getDoc(orderRef);
    return { id: updatedOrderDoc.id, ...updatedOrderDoc.data() };
  } catch (error) {
    console.error('Error unarchiving order:', error);
    throw new Error('Error al desarchivar la orden');
  }
};

// ===== BOOKMARK OPERATIONS =====

/**
 * Toggles bookmark status for an order
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Updated order object
 */
export const toggleOrderBookmark = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Orden no encontrada');
    }
    
    const orderData = orderDoc.data();
    const currentBookmarked = orderData.isBookmarked || false;
    
    // Toggle bookmark status
    await updateDoc(orderRef, {
      isBookmarked: !currentBookmarked,
      updatedAt: new Date()
    });
    
    // Update local cache
    try {
      const allOrders = await getAllOrdersFromIndexedDB();
      const updatedOrders = allOrders.map(order => 
        order.id === orderId 
          ? { ...order, isBookmarked: !currentBookmarked, updatedAt: new Date() }
          : order
      );
      await syncOrdersToIndexedDB(updatedOrders);
    } catch (cacheError) {
      console.warn('Could not update bookmark in local cache:', cacheError);
    }
    
    // Log activity
    await logActivity(
      !currentBookmarked ? ACTIVITY_TYPES.ORDER_UPDATED : ACTIVITY_TYPES.ORDER_UPDATED, 
      `Orden #${orderData.folio} ${!currentBookmarked ? 'marcada' : 'desmarcada'} como favorita`, 
      {
        orderId,
        folio: orderData.folio,
        bookmarked: !currentBookmarked
      }
    );
    
    // Get updated order
    const updatedOrderDoc = await getDoc(orderRef);
    return { id: updatedOrderDoc.id, ...updatedOrderDoc.data() };
  } catch (error) {
    console.error('Error toggling bookmark:', error);
    throw new Error('Error al cambiar marcador de orden');
  }
};

// Cache timestamp functions removed to prevent excessive Firestore reads
// The app now uses simple cache-first approach: use IndexedDB if available, otherwise fetch from Firestore